/**
 * O que a pessoa lê quando uma ação falha: toast, aviso vermelho, faixa de erro.
 *
 * Por que existe: dezenas de telas faziam `toast.error(err.message)` ou
 * `setError(err.message)`. O texto que chegava à tela era o do banco, da
 * biblioteca ou do navegador, em inglês ou com nome interno:
 * "new row violates row-level security policy", "JWT expired",
 * "Edge Function returned a non-2xx status code", "tenant_id ausente",
 * "Load failed". O tomador que preenchia a página de cadastro lia
 * "(Load failed)" no fim do aviso (25 vezes em 90 dias, medido em 06/10/2026).
 * Isso não diz a ninguém o que fazer e expõe o interior do sistema
 * (`.claude/rules/user-facing-communication.md`, CLAUDE.md §5.3.1).
 *
 * Quatro regras moram aqui, e nenhuma tela deve reimplementá-las:
 *
 * 1. **Frase escrita para a pessoa passa.** Quem quer mostrar um motivo
 *    específico lança `AvisoAoUsuario`, e a frase chega à tela como foi escrita.
 * 2. **Frase em português que já é de tela também passa**, para não perder os
 *    motivos que o código já lançava com `new Error("Já existe…")`. O teste é
 *    conservador: sem acento, com nome técnico, de fornecedor ou com marca de
 *    código, não passa.
 * 3. **Sessão, permissão e rede viram frase com o que fazer**, porque nesses
 *    três casos a pessoa consegue agir.
 * 4. **Todo o resto vira o texto de quem chamou.** O erro original vai para o
 *    console, onde nós o lemos — nunca para a tela.
 */

import { SESSION_EXPIRED_MESSAGE } from "@/lib/auth-errors"

/**
 * Erro cuja mensagem foi escrita para a pessoa ler. Lance este, e não `Error`,
 * quando o motivo da recusa é algo que ela pode resolver ("Já existe um lead
 * com este CPF nesta unidade").
 */
export class AvisoAoUsuario extends Error {
  readonly paraUsuario = true as const

  constructor(mensagem: string) {
    super(mensagem)
    this.name = "AvisoAoUsuario"
  }
}

export const MENSAGEM_SEM_PERMISSAO =
  "Seu usuário não tem permissão para fazer isso. Se precisar, fale com o administrador da sua ESC."

export const MENSAGEM_SEM_CONEXAO =
  "Não consegui falar com o servidor. Confira sua conexão com a internet e tente de novo."

export { SESSION_EXPIRED_MESSAGE as MENSAGEM_SESSAO_EXPIRADA }

/** Sublinhado entre letras (nome de campo), chaves, aspas, endereço, número longo. */
const MARCA_TECNICA = /[A-Za-z0-9]_[A-Za-z0-9]|[{}[\]<>`|\\'"]|:\/\/|\d{3,}/

/** Palavra que só aparece em mensagem de máquina ou que nomeia fornecedor. */
const PALAVRA_TECNICA =
  /\b(?:error|errors|details|edge|function|functions|token|webhook|json|sql|https?|status|exception|undefined|null|nan|stack|supabase|postgres|postgrest|deno|graph|api|oauth|waba|sdk|jwt|rls|rpc|policy|constraint|violates|duplicate|schema|column|relation|invalid|failed|fetch|network|timeout|missing|required|tenant|role|users?|payload|bucket|storage|uuid|unknown|doctype|array|sweep|bigdatacorp|gemini|groq|frappe|apify|openrouter|resend|cloudflare)\b/i

/**
 * Nome de variável em camelCase ("leadId", "tenantId"). As marcas que a tela
 * escreve assim de propósito são tiradas antes da conferência.
 */
const CAMEL_CASE = /[a-z][A-Z]/
const MARCAS_COM_MAIUSCULA = /\b(?:CreditOS|WhatsApp|iFood|LinkedIn|YouTube|TikTok|iPhone)\b/g

/** Sem acento nenhum, a frase quase sempre é inglês ou código. */
const ACENTO_PORTUGUES = /[áàâãéêíóôõúç]/i

/**
 * O texto já é frase de tela? Conservadora de propósito: na dúvida recusa, e a
 * pessoa lê o texto de quem chamou, que diz a mesma coisa sem o detalhe.
 */
export function pareceFraseDeTela(texto: unknown): texto is string {
  if (typeof texto !== "string") return false
  const t = texto.trim()
  if (t.length < 8 || t.length > 240) return false
  if (MARCA_TECNICA.test(t) || PALAVRA_TECNICA.test(t)) return false
  if (CAMEL_CASE.test(t.replace(MARCAS_COM_MAIUSCULA, ""))) return false
  return ACENTO_PORTUGUES.test(t)
}

interface ErroLike {
  name?: unknown
  message?: unknown
  code?: unknown
  status?: unknown
}

function comoObjeto(err: unknown): ErroLike {
  if (err && typeof err === "object") return err as ErroLike
  if (typeof err === "string") return { message: err }
  return {}
}

function texto(valor: unknown): string {
  return typeof valor === "string" ? valor : ""
}

function ehAvisoAoUsuario(err: unknown): err is { message: string } {
  return (
    err instanceof AvisoAoUsuario ||
    (!!err && typeof err === "object" && (err as { paraUsuario?: unknown }).paraUsuario === true &&
      typeof (err as { message?: unknown }).message === "string")
  )
}

/** O que deu errado, do ponto de vista de quem pode agir. */
export type TipoDeFalha = "sessao" | "permissao" | "rede" | "outra"

/** Classifica a falha sem olhar se ela é frase de tela. Nunca lança. */
export function tipoDaFalha(err: unknown): TipoDeFalha {
  const e = comoObjeto(err)
  const nome = texto(e.name)
  const codigo = texto(e.code)
  const msg = texto(e.message).toLowerCase()
  const status = typeof e.status === "number" ? e.status : undefined

  if (
    codigo === "PGRST301" ||
    codigo === "PGRST303" ||
    nome === "AuthSessionMissingError" ||
    nome === "AuthInvalidJwtError" ||
    status === 401 ||
    /jwt expired|invalid jwt|jwt is expired|session (?:missing|expired|not found)|refresh token|\b(?:invalid_session|session_expired|session_not_found)\b/.test(msg)
  )
    return "sessao"

  if (
    codigo === "42501" ||
    status === 403 ||
    /row-level security|permission denied|insufficient privilege|not authorized|^forbidden\b|unauthorized: requires|permissão insuficiente/.test(msg)
  )
    return "permissao"

  const semRede = typeof navigator !== "undefined" && navigator.onLine === false
  if (
    semRede ||
    nome === "FunctionsFetchError" ||
    nome === "AuthRetryableFetchError" ||
    /failed to fetch|load failed|networkerror|network request failed|failed to send a request/.test(msg)
  )
    return "rede"

  return "outra"
}

/**
 * A frase que a tela mostra quando `err` aconteceu. Nunca devolve o texto cru
 * de um erro de máquina: quando não há frase melhor, devolve `textoPadrao`.
 *
 * @param err          o que foi capturado no `catch` (ou o `error` do supabase-js)
 * @param textoPadrao  o efeito para a pessoa, em português ("Não foi possível
 *                     salvar a regra. Tente de novo em instantes.")
 * @param contexto     onde aconteceu, para o console ("[ConfigSOMA] salvar")
 */
export function mensagemParaUsuario(err: unknown, textoPadrao: string, contexto?: string): string {
  if (ehAvisoAoUsuario(err)) return err.message

  // O original fica registrado para nós. Nunca para a pessoa.
  console.error(`[${contexto ?? "erro"}]`, err)

  const original = texto(comoObjeto(err).message)
  if (pareceFraseDeTela(original)) return original.trim()

  switch (tipoDaFalha(err)) {
    case "sessao":
      return SESSION_EXPIRED_MESSAGE
    case "permissao":
      return MENSAGEM_SEM_PERMISSAO
    case "rede":
      return MENSAGEM_SEM_CONEXAO
    case "outra":
      return textoPadrao
  }
}
